//--------------------------------------------------------------------//
//******************** providerInfoTab ***********************************//
//--------------------------------------------------------------------//
// Displays and manages editable provider properties: name (inline edit/
// save/cancel, mirrors trunk-info.tab.js's saveField), and - for
// generic_imap providers only - the IMAP/SMTP host template (edited as one
// block rather than per-field, since these four host/port pairs are always
// changed together). `type` is shown read-only after creation - switching a
// provider between Google and generic_imap after accounts already exist
// under it would orphan their imap/smtp or google_identity_ref fields, so
// it's fixed at creation time (see add-email-provider-dialog.template.html).
// No delete button here - deletion lives on the outer provider row (mirrors
// sipTrunksTable.directive.js's showDeleteTrunkPopup, not trunk-info.tab.js).
import providerInfoTemplateUrl from './providerInfo.template.html';

angular.module('emailSettingsModule')

.directive('providerInfoTab', ['EmailSettingsService', 'GhDialog', function (EmailSettingsService, GhDialog) {
    return {
        restrict: 'E',
        scope: {
            provider: '=',
            onUpdate: '&'
        },
        controller: ['$scope', '$element', '$animate', 'localizationService', function ($scope, $element, $animate, localizationService) {
            const dictionary = localizationService.getDictionary('account_settings');
            $scope.translate = (word) => localizationService.translate(word, dictionary);

            // Same ngAnimate ng-if-swap workaround as trunk-info.tab.js.
            $animate.enabled(false, $element);

            $scope.provider.generic = angular.extend(
                { imap_host: '', imap_port: 993, imap_secure: true, smtp_host: '', smtp_port: 465, smtp_secure: true },
                $scope.provider.generic || {}
            );
            $scope.provider.google_workspace = $scope.provider.google_workspace || { configured: false };

            // Shared with emailProviders.directive.js's own Type column -
            // kept in sync manually (no shared constant module exists yet
            // for the 3 provider types).
            $scope.providerTypeLabel = function (type) {
                if (type === 'google') return $scope.translate('Gmail / Google Workspace (individual sign-in)');
                if (type === 'google_workspace') return $scope.translate('Google Workspace (Admin)');
                return $scope.translate('Generic IMAP / SMTP');
            };

            $scope.editingName = false;
            $scope.editingHosts = false;
            $scope.editingServiceAccount = false;
            $scope.savingToggle = false;
            $scope.savingServiceAccount = false;
            $scope.serviceAccountError = '';
            $scope.nameDraft = $scope.provider.name || '';
            $scope.hostsDraft = angular.copy($scope.provider.generic);
            $scope.serviceAccountDraft = '';

            const safeApply = function () {
                $scope.$applyAsync();
            };

            function showError(message) {
                GhDialog.show({
                    position: 'center',
                    toolbar: false,
                    template: {
                        toolbar: '',
                        resizable: false,
                        content: `
                            <div class="delete_sip_trunk-popup">
                                <div class="dialog-share-group">
                                    <p class="dialog-share-group__title">Error</p>
                                </div>
                                <div class="dialog-share-group__content">
                                    <p class="info">{{errorMessage}}</p>
                                    <div class="dialog-share-group__content__buttons">
                                        <button class="btn btn-blue" ng-click="cancel()">OK</button>
                                    </div>
                                </div>
                            </div>
                        `
                    },
                    locals: { errorMessage: message },
                    controller: ['$scope', 'errorMessage', function ($scope, errorMessage) {
                        $scope.errorMessage = errorMessage;
                    }]
                });
            }

            $scope.enableEditName = function () {
                $scope.nameDraft = $scope.provider.name || '';
                $scope.editingName = true;
            };

            $scope.cancelEditName = function () {
                $scope.editingName = false;
            };

            $scope.saveName = async function () {
                const name = ($scope.nameDraft || '').trim();
                if (!name) return;

                try {
                    const updated = await EmailSettingsService.updateProvider($scope.provider.provider_id, { name });
                    Object.assign($scope.provider, updated);
                    $scope.editingName = false;
                    safeApply();
                    $scope.onUpdate();
                } catch (error) {
                    console.log('Update provider error:', error);
                    showError(error.message || 'Failed to update provider');
                }
            };

            $scope.enableEditHosts = function () {
                $scope.hostsDraft = angular.copy($scope.provider.generic);
                $scope.editingHosts = true;
            };

            $scope.cancelEditHosts = function () {
                $scope.editingHosts = false;
            };

            $scope.saveHosts = async function () {
                try {
                    const updated = await EmailSettingsService.updateProvider($scope.provider.provider_id, { generic: $scope.hostsDraft });
                    Object.assign($scope.provider, updated);
                    $scope.editingHosts = false;
                    safeApply();
                    $scope.onUpdate();
                } catch (error) {
                    console.log('Update provider error:', error);
                    showError(error.message || 'Failed to update provider');
                }
            };

            // Write-only: the server never returns the actual service
            // account key back (see EmailDatabase._stripProviderSecrets),
            // only a `configured` boolean - so the draft always starts
            // blank, whether or not one is already saved. Pasting a new key
            // here always fully replaces the previous one.
            $scope.enableEditServiceAccount = function () {
                $scope.serviceAccountDraft = '';
                $scope.serviceAccountError = '';
                $scope.editingServiceAccount = true;
            };

            $scope.cancelEditServiceAccount = function () {
                $scope.editingServiceAccount = false;
                $scope.serviceAccountError = '';
            };

            $scope.saveServiceAccount = async function () {
                const raw = ($scope.serviceAccountDraft || '').trim();

                if (!raw) {
                    $scope.serviceAccountError = 'Please paste the service account JSON key';
                    return;
                }

                try {
                    JSON.parse(raw);
                } catch (parseError) {
                    $scope.serviceAccountError = 'That doesn\'t look like valid JSON';
                    return;
                }

                $scope.savingServiceAccount = true;
                $scope.serviceAccountError = '';

                try {
                    const updated = await EmailSettingsService.updateProvider($scope.provider.provider_id, {
                        google_workspace: { service_account_json: raw }
                    });
                    Object.assign($scope.provider, updated);
                    $scope.editingServiceAccount = false;
                    $scope.onUpdate();
                } catch (error) {
                    console.log('Update provider error:', error);
                    $scope.serviceAccountError = error.message || 'Failed to save service account key';
                } finally {
                    $scope.savingServiceAccount = false;
                    safeApply();
                }
            };

            $scope.toggleEnabled = async function () {
                $scope.savingToggle = true;

                try {
                    const updated = await EmailSettingsService.updateProvider($scope.provider.provider_id, { enabled: $scope.provider.enabled });
                    Object.assign($scope.provider, updated);
                    $scope.onUpdate();
                } catch (error) {
                    console.log('Update provider error:', error);
                    $scope.provider.enabled = !$scope.provider.enabled;
                    showError(error.message || 'Failed to update provider');
                } finally {
                    $scope.savingToggle = false;
                    safeApply();
                }
            };
        }],
        templateUrl: providerInfoTemplateUrl
    };
}]);
